// v10/src/components/admin/lifespan/LifespanDashboardWidget.js
//
// A6.2 parte 2B — Widget de resumen mantenimiento en la pantalla principal
// de A6.1 (LifespanTab). Lee `/api/admin/alerts/dashboard` (alertsService)
// y muestra: counts por estado, aeronaves total/AOG, próximos a vencer
// 30/60/90d, top 3 aeronaves con más alertas.
//
// Decisión de scope: sin polling automático (post-demo). Se refresca al
// montar y cuando el usuario aprieta el botón de refresh.

import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle, AlertTriangle, Flame, XOctagon,
  Plane, AlertOctagon, RefreshCw, Loader,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { alertsService } from '../../../services/notificationPreferencesService';
import AogBadge from '../../aircraft/AogBadge';

const StatCard = ({ icon: Icon, label, value, color, darkMode }) => (
  <div
    style={{
      display: 'flex', flexDirection: 'column', gap: 4,
      padding: '10px 12px', borderRadius: 14,
      background: darkMode ? `${color}14` : `${color}0d`,
      border: `1px solid ${color}26`,
      minWidth: 0,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <Icon style={{ width: 14, height: 14, color }} />
      <span style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </span>
    </div>
    <span style={{ fontSize: 22, fontWeight: 900, color: darkMode ? 'white' : '#0f172a', lineHeight: 1 }}>
      {value}
    </span>
  </div>
);

const TrendBox = ({ value, label, darkMode }) => (
  <div
    style={{
      flex: 1,
      padding: '10px 12px', borderRadius: 12,
      background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc',
      border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
      textAlign: 'center',
      minWidth: 0,
    }}
  >
    <p style={{ margin: 0, fontSize: 18, fontWeight: 900, color: darkMode ? 'white' : '#0f172a', lineHeight: 1 }}>
      {value}
    </p>
    <p style={{ margin: '4px 0 0', fontSize: 11, fontWeight: 600, color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }}>
      {label}
    </p>
  </div>
);

const WidgetSkeleton = ({ darkMode }) => (
  <div
    style={{
      padding: 20, borderRadius: 20,
      background: darkMode ? 'rgba(255,255,255,0.03)' : 'white',
      border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: 90, gap: 10,
      color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b',
    }}
  >
    <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
    <span style={{ fontSize: 13 }}>Cargando…</span>
    <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
  </div>
);

const WidgetError = ({ error, onRetry, darkMode }) => (
  <div
    style={{
      padding: 18, borderRadius: 20,
      background: 'rgba(239,68,68,0.06)',
      border: '1px solid rgba(239,68,68,0.2)',
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    }}
  >
    <AlertTriangle style={{ width: 18, height: 18, color: '#ef4444', flexShrink: 0 }} />
    <span style={{ fontSize: 13, color: '#dc2626', flex: 1, minWidth: 0 }}>{error}</span>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        style={{
          padding: '6px 12px', borderRadius: 10,
          border: '1px solid rgba(239,68,68,0.3)',
          background: 'transparent',
          color: '#dc2626', fontWeight: 700, fontSize: 12,
          cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        <RefreshCw style={{ width: 12, height: 12, marginRight: 6, verticalAlign: 'middle' }} />
        Reintentar
      </button>
    )}
  </div>
);

const LifespanDashboardWidget = ({ darkMode }) => {
  const { t } = useTranslation('admin');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [refreshing, setRef]  = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRef(silent);
    setError('');
    try {
      const res = await alertsService.getDashboard();
      setData(res?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || t('dashboard_widget_error'));
    } finally {
      setLoading(false);
      setRef(false);
    }
  }, [t]);

  useEffect(() => { load(false); }, [load]);

  if (loading) return <WidgetSkeleton darkMode={darkMode} />;
  if (error)   return <WidgetError error={error} onRetry={() => load(false)} darkMode={darkMode} />;
  if (!data)   return null;

  const summary  = data.summary  || {};
  const trends   = data.trends   || {};
  const topAircraft = Array.isArray(data.top_aircraft_with_alerts)
    ? data.top_aircraft_with_alerts
    : [];

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8';

  const hasTrends = (trends.components_expiring_next_30d || 0)
    + (trends.components_expiring_next_60d || 0)
    + (trends.components_expiring_next_90d || 0) > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        padding: 20, borderRadius: 20,
        background: darkMode ? 'rgba(255,255,255,0.03)' : 'white',
        border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
        boxShadow: darkMode ? 'none' : '0 2px 12px rgba(0,0,0,0.04)',
        display: 'flex', flexDirection: 'column', gap: 16,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: fgPrimary, letterSpacing: '-0.2px' }}>
            {t('dashboard_widget_title')}
          </h3>
          {data.org?.name && (
            <p style={{ margin: '2px 0 0', fontSize: 12, color: fgMuted }}>
              {data.org.name}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={refreshing}
          aria-label={t('dashboard_widget_refresh')}
          title={t('dashboard_widget_refresh')}
          style={{
            padding: 8, borderRadius: 10,
            border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
            background: 'transparent',
            cursor: refreshing ? 'not-allowed' : 'pointer',
            color: fgMuted, display: 'flex',
          }}
        >
          <RefreshCw style={{ width: 14, height: 14, animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
        </button>
      </div>

      {/* Stats principales */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
          gap: 10,
        }}
      >
        <StatCard icon={CheckCircle}   label={t('dashboard_widget_operational')} value={summary.operational ?? 0} color="#16a34a" darkMode={darkMode} />
        <StatCard icon={AlertTriangle} label={t('dashboard_widget_next')}        value={summary.next        ?? 0} color="#f59e0b" darkMode={darkMode} />
        <StatCard icon={Flame}         label={t('dashboard_widget_critical')}    value={summary.critical    ?? 0} color="#ea580c" darkMode={darkMode} />
        <StatCard icon={XOctagon}      label={t('dashboard_widget_expired')}     value={summary.expired     ?? 0} color="#dc2626" darkMode={darkMode} />
      </div>

      {/* Aeronaves */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', fontSize: 13, color: fgMuted }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plane style={{ width: 14, height: 14 }} />
          <strong style={{ color: fgPrimary, fontWeight: 700 }}>{summary.aircraft_total ?? 0}</strong>
          {t('dashboard_widget_aircraft_total_suffix')}
        </span>
        {(summary.aircraft_aog ?? 0) > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#dc2626', fontWeight: 700 }}>
            <AlertOctagon style={{ width: 14, height: 14 }} />
            {t('dashboard_widget_aircraft_aog', { count: summary.aircraft_aog })}
          </span>
        )}
      </div>

      {/* Trends */}
      {hasTrends && (
        <div>
          <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 800, color: fgFaint, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {t('dashboard_widget_trends_title')}
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <TrendBox value={trends.components_expiring_next_30d || 0} label={t('dashboard_widget_trend_30d')} darkMode={darkMode} />
            <TrendBox value={trends.components_expiring_next_60d || 0} label={t('dashboard_widget_trend_60d')} darkMode={darkMode} />
            <TrendBox value={trends.components_expiring_next_90d || 0} label={t('dashboard_widget_trend_90d')} darkMode={darkMode} />
          </div>
        </div>
      )}

      {/* Top aircraft */}
      {topAircraft.length > 0 && (
        <div>
          <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 800, color: fgFaint, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {t('dashboard_widget_top_aircraft_title')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {topAircraft.slice(0, 3).map((ac) => (
              <div
                key={ac.aircraft_id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  justifyContent: 'space-between',
                  padding: '8px 12px', borderRadius: 12,
                  background: darkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                  border: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #e2e8f0',
                }}
              >
                <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 800, fontFamily: 'monospace', fontSize: 13, color: fgPrimary }}>
                    {ac.registration || '—'}
                  </span>
                  {ac.model && (
                    <span style={{ fontSize: 12, color: fgMuted }}>{ac.model}</span>
                  )}
                  <AogBadge aircraft={ac} size="sm" />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: fgMuted, flexShrink: 0 }}>
                  <span>{t('dashboard_widget_aircraft_alerts', { count: ac.alerts_count || 0 })}</span>
                  {(ac.components_expired || 0) > 0 && (
                    <span style={{ color: '#dc2626', fontWeight: 700 }}>
                      · {t('dashboard_widget_aircraft_expired', { count: ac.components_expired })}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default LifespanDashboardWidget;
